// AnwesenheitCard — Präsenz-Fortschritt für Kurse mit Präsenzpflicht (z. B. Basiskurs Intensivpflege)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, CheckCircle2, Download, Loader2, MapPin, Video } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; } };
const fmtH = (h) => String(h).replace('.', ',');

export default function AnwesenheitCard({ courseId, getAuthHeader }) {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/user/anwesenheit/${courseId}`, getAuthHeader())
      .then((r) => setData(r.data)).catch(() => {});
  }, [courseId]);

  if (!data?.enabled) return null;

  const { required_hours, praesenz_hours, online_hours, sessions } = data;
  const pct = Math.min(100, Math.round((praesenz_hours / required_hours) * 100));
  const done = praesenz_hours >= required_hours;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/user/anwesenheit/${courseId}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Anwesenheitsnachweis.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
    finally { setDownloading(false); }
  };

  return (
    <aside className="mb-8 rounded-xl bg-cream-100 border border-cream-300" data-testid="anwesenheit-card">
      <div className="px-5 sm:px-6 py-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-terra/10 flex items-center justify-center">
            {done ? <CheckCircle2 className="w-5 h-5 text-green-700" strokeWidth={1.7} />
              : <CalendarCheck className="w-5 h-5 text-terra" strokeWidth={1.7} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-sand-500 mb-0.5">
              Präsenzpflicht · {required_hours} Zeitstunden
            </div>
            <div className="text-sm sm:text-[14.5px] text-espresso/90 leading-snug">
              <span className="font-semibold text-espresso">Präsenz-Anwesenheit</span>{' '}
              — von deiner Dozentin dokumentierte Termine.
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className={`font-bold ${done ? 'text-green-700' : 'text-espresso'}`} data-testid="anwesenheit-progress-label">
                  {fmtH(praesenz_hours)} / {required_hours} Std. in Präsenz {done && '· erfüllt ✓'}
                </span>
                {online_hours > 0 && <span className="text-sand-500">+ {fmtH(online_hours)} Std. online</span>}
              </div>
              <div className="h-2 rounded-full bg-cream-300 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${done ? 'bg-green-600' : 'bg-terra'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
          {sessions.length > 0 && (
            <button onClick={downloadPdf} disabled={downloading}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-espresso text-cream-50 hover:opacity-90 shrink-0"
              data-testid="anwesenheit-pdf-btn">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Nachweis
            </button>
          )}
        </div>

        {sessions.length > 0 && (
          <div className="mt-4 space-y-1.5" data-testid="anwesenheit-sessions">
            {sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] bg-cream-50 border border-cream-300 rounded-lg px-3.5 py-2">
                {s.session_type === 'praesenz'
                  ? <MapPin className="w-3.5 h-3.5 text-terra shrink-0" />
                  : <Video className="w-3.5 h-3.5 text-sand-500 shrink-0" />}
                <span className="font-semibold text-espresso whitespace-nowrap">{fmtDate(s.date)}</span>
                {s.time_from && <span className="text-sand-500 whitespace-nowrap">{s.time_from}–{s.time_to}</span>}
                <span className="text-espresso/80 truncate flex-1">{s.topic || (s.session_type === 'praesenz' ? 'Präsenztermin' : 'Online-Termin')}</span>
                <span className="text-sand-500 whitespace-nowrap">{fmtH(s.hours)} Std.</span>
              </div>
            ))}
          </div>
        )}
        {sessions.length === 0 && (
          <p className="mt-3 text-xs text-sand-500" data-testid="anwesenheit-empty">
            Noch keine Termine dokumentiert — deine Dozentin trägt Präsenzzeiten nach jedem Termin ein.
          </p>
        )}
      </div>
    </aside>
  );
}
